import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import NeoButton from "../../../components/Common/NeoButton";
import NeoTable from "../../../components/Common/NeoTable";
import NeoCard from "../../../components/Common/NeoCard";
import NeoSearch from "../../../components/Common/NeoSearch";
import NeoDropdown from "../../../components/Common/NeoDropdown";
import NeoBadge from "../../../components/Common/NeoBadge";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import ClientFormModal from "./components/ClientFormModal";
import usePlatformTenants from "./hooks/usePlatformTenants";
import { TENANT_STATUS_LABELS, TENANT_STATUS_COLORS, STATUS_FILTER_OPTIONS } from "./constants";

const TRIAL_BILLING_STATUSES = new Set(["trial", "trial_started", "trial_active", "trialing"]);

const getCalendarISO = (value) => {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().split("T")[0];
};

const resolveTenantStatus = (tenant) => {
    if (tenant.status) return tenant.status;
    if (tenant.accessBlockedAt || tenant.active === false) return "blocked";

    const billingStatus = String(tenant.billingStatus || tenant.checkoutStatus || "").trim().toLowerCase();
    const trialStartDate = getCalendarISO(tenant.trialStartDate);
    const trialEndsAt = getCalendarISO(tenant.trialEndsAt);
    const todayISO = new Date().toLocaleDateString("en-CA");

    if (TRIAL_BILLING_STATUSES.has(billingStatus) || trialEndsAt) {
        if (trialStartDate && trialStartDate > todayISO) return "trial_scheduled";
        if (trialEndsAt && trialEndsAt < todayISO) return "expired";
        return "trial";
    }

    if (billingStatus === "cancelled" || billingStatus === "canceled") return "cancelled";
    if (billingStatus === "expired") return "expired";
    if (billingStatus === "overdue" || billingStatus === "past_due" || billingStatus === "unpaid") return "inadimplente";
    if (billingStatus === "manual_active" || billingStatus === "active" || billingStatus === "approved" || tenant.active) return "active";

    return billingStatus || "draft";
};

const PlatformTenants = () => {
    const navigate = useNavigate();
    const { items, loading, createNewTenant } = usePlatformTenants();

    const [search, setSearch] = useState("");
    const [searchParams] = useSearchParams();
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
    const [modalOpen, setModalOpen] = useState(false);

    const tenantsWithStatus = items.map((tenant) => ({
        ...tenant,
        status: resolveTenantStatus(tenant),
        responsibleName: tenant.responsibleName || tenant.owner?.fullName || tenant.ownerName || "",
    }));

    const filteredTenants = tenantsWithStatus.filter((t) => {
        const q = search.toLowerCase();
        const matchesSearch =
            (t.name || "").toLowerCase().includes(q) ||
            (t.document || "").includes(search) ||
            (t.responsibleName || "").toLowerCase().includes(q) ||
            (t.email || "").toLowerCase().includes(q);
        const matchesStatus = statusFilter === "all" || t.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleCreateTenant = async (formData) => {
        await createNewTenant(formData);
        setModalOpen(false);
    };

    const columns = [
        {
            key: "name",
            label: "Nome da Academia",
            width: "35%",
            render: (row) => (
                <div className="d-flex align-items-center gap-3">
                    {row.photoUrl ? (
                        <img
                            src={row.photoUrl}
                            alt={row.name}
                            className="rounded-circle"
                            style={{ width: "40px", height: "40px", minWidth: "40px", objectFit: "cover", boxShadow: "0 2px 5px rgba(0,0,0,0.15)" }}
                        />
                    ) : (
                        <div
                            className="avatar-title text-white rounded-circle d-flex align-items-center justify-content-center fw-bold font-size-14"
                            style={{ width: "40px", height: "40px", minWidth: "40px", background: "linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-accent) 100%)", boxShadow: "0 2px 5px rgba(0,0,0,0.15)" }}
                        >
                            {(row.name || "?").substring(0, 2).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div className="fw-bold text-dark font-size-14">{row.name}</div>
                        <span className="text-xs text-muted d-block">{row.corporateName}</span>
                        <span className="text-xs text-muted platform-mono">{row.document}</span>
                    </div>
                </div>
            ),
        },
        {
            key: "responsibleName",
            label: "Responsável / Contato",
            width: "25%",
            render: (row) => (
                <div>
                    <div className="fw-semibold text-secondary">{row.responsibleName}</div>
                    <span className="text-xs text-muted d-block">{row.phone}</span>
                    <span className="text-xs text-muted d-block">{row.email}</span>
                </div>
            ),
        },
        {
            key: "planId",
            label: "Plano",
            width: "15%",
            render: (row) => <span className="fw-semibold" style={{ color: "var(--color-brand-primary)" }}>{row.planId || row.plan}</span>,
        },
        {
            key: "status",
            label: "Status",
            width: "15%",
            render: (row) => (
                <div>
                    <NeoBadge color={TENANT_STATUS_COLORS[row.status] || "muted"}>
                        {TENANT_STATUS_LABELS[row.status] || row.status}
                    </NeoBadge>
                </div>
            ),
        },
        {
            key: "actions",
            label: "Ações",
            width: "10%",
            align: "center",
            render: (row) => (
                <NeoButton
                    variant="soft-secondary"
                    size="sm"
                    square={true}
                    icon="mdi-eye-outline"
                    onClick={() => navigate(`/platform/tenants/${row.id}`)}
                    title="Detalhes"
                />
            ),
        },
    ];

    const currentStatusLabel = STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter)?.label || "Status";

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Clientes / Academias" />
            </div>

            <NeoCard
                title="Lista de Clientes / Academias"
                subtitle="Gerencie todas as academias e assessorias cadastradas no SaaS NeoApp."
                headerIcon="mdi-domain"
                headerActions={
                    <NeoButton variant="primary" size="sm" onClick={() => setModalOpen(true)}>
                        <i className="ph-light ph-plus fs-5 me-1" /> Novo Cliente
                    </NeoButton>
                }
                thinBorder={true}
                bodyClassName="p-0"
            >
                <NeoTable
                    columns={columns}
                    data={filteredTenants}
                    loading={loading}
                    defaultItemsPerPage={20}
                    itemsPerPageOptions={[20, 50, 100]}
                    emptyMessage="Nenhuma academia encontrada"
                    emptySubMessage="Ajuste os filtros de status ou a pesquisa para encontrar registros."
                    searchComponent={
                        <div className="d-flex align-items-center gap-3">
                            <NeoSearch
                                size="sm"
                                placeholder="Buscar academia..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClear={() => setSearch("")}
                                style={{ width: "240px" }}
                            />
                            <div className="d-flex align-items-center gap-2">
                                <span className="neo-table-topbar-label m-0">Status:</span>
                                <NeoDropdown
                                    label={currentStatusLabel}
                                    variant="soft-secondary"
                                    size="sm"
                                    items={STATUS_FILTER_OPTIONS.map(o => ({
                                        label: o.label,
                                        onClick: () => setStatusFilter(o.value),
                                    }))}
                                />
                            </div>
                        </div>
                    }
                />
            </NeoCard>

            <ClientFormModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                onSave={handleCreateTenant}
            />
        </div>
    );
};

export default PlatformTenants;
